import React from 'react';
import styled from 'styled-components';

// SEGMENTED — una scelta fra poche alternative viste tutte insieme.
//
// È UN oggetto diviso in scomparti: cornice condivisa, uno scomparto acceso. Non è una
// fila di interruttori, ed è la ragione di `role="radiogroup"` più `aria-checked`: chi
// legge con la tastiera deve sentire «una fra tre», non «tre indipendenti».
//
// Non è ZTabs. Le linguette cambiano CIÒ CHE SI VEDE sotto di loro; questo cambia il
// VALORE di una proprietà che sta sulla sua riga. Con le linguette al posto suo, una
// preferenza si legge come una navigazione — ed è quello che succedeva nella dialog
// delle impostazioni prima del 2026-08-12.
//
// La variante a icone e quella a parole sono lo stesso componente: cambia solo se lo
// scomparto è largo quanto un glifo o quanto la sua parola.

// Il fondo dello scomparto acceso è la pagina, quello del gruppo è più scuro: senza i due
// toni distinti la pastiglia accesa non si vede, ed era il difetto della prima versione —
// bianco su bianco, con la sola ombra a dire quale delle tre fosse scelta.
const Group = styled.div`
    display: flex;
    flex-shrink: 0;
    gap: 2px;
    padding: 3px;
    border-radius: 999px;
    background: ${(props) => props.theme.app.backgroundToolbar};
    box-shadow: inset 0 0 0 1px ${(props) => props.theme.app.borderColor};
`;

const Segment = styled.button<{ $checked: boolean; $withLabel: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${(props) => (props.$withLabel ? 'auto' : '26px')};
    min-width: 26px;
    height: 22px;
    padding: ${(props) => (props.$withLabel ? '0 10px' : '0')};
    border: 0;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    color: ${(props) => (props.$checked ? props.theme.app.textColor : props.theme.app.primary)};
    background: ${(props) => (props.$checked ? props.theme.app.backgroundColor : 'transparent')};
    box-shadow: ${(props) => (props.$checked ? '0 1px 3px rgba(38, 50, 56, 0.22)' : 'none')};
    opacity: ${(props) => (props.$checked ? 1 : 0.62)};
    transition:
        color 140ms ease,
        background 140ms ease,
        box-shadow 140ms ease;

    &:hover,
    &:focus-visible {
        color: ${(props) => props.theme.app.textColor};
        opacity: 1;
        outline: none;
    }

    // I glifi del prototipo sono a contorno e non passano dal wrapper Icon condiviso,
    // che li riempirebbe: qui si dà la misura e basta.
    svg {
        width: 14px;
        height: 14px;
        display: block;
    }
`;

export interface SegmentedOption<T extends string> {
    value: T;
    /** Il glifo, per la variante a icone. */
    icon?: React.ReactNode;
    /** La parola, per la variante a parole. Serve comunque come etichetta accessibile. */
    label: string;
}

export interface SegmentedProps<T extends string> {
    options: SegmentedOption<T>[];
    value: T;
    onChange: (value: T) => void;
    /** Cosa si sta scegliendo, per chi non vede gli scomparti. */
    'aria-label': string;
    className?: string;
}

export function Segmented<T extends string>({ options, value, onChange, className, ...rest }: SegmentedProps<T>) {
    return (
        <Group role='radiogroup' aria-label={rest['aria-label']} className={className}>
            {options.map((option) => (
                <Segment
                    key={option.value}
                    type='button'
                    role='radio'
                    aria-checked={option.value === value}
                    aria-label={option.icon ? option.label : undefined}
                    title={option.label}
                    $checked={option.value === value}
                    $withLabel={!option.icon}
                    onClick={() => onChange(option.value)}
                >
                    {option.icon ?? option.label}
                </Segment>
            ))}
        </Group>
    );
}

export default Segmented;
